import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
  Checkbox,
  Collapse,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import HistoryIcon from "@mui/icons-material/History";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { toast } from "react-toastify";
import { SidebarCol } from "../../../screens/chatMain/Sidebar/Sidebar.jsx";
import { MainHeader } from "../../../screens/chatMain/MainHeader/MainHeader.jsx";
import { RichTextEditor } from "./RichTextEditor";
import {
  fetchEmailOrganizations,
  resolveEmailRecipients,
  sendEmailBroadcast,
  sendTestEmail,
  fetchSentEmailHistory,
} from "../../../api/dev/emailBroadcastApi";

const DevSendEmail = () => {
  const navigate = useNavigate();

  // Primary Tabs: 0 = Compose, 1 = History
  const [activeTab, setActiveTab] = useState(0);

  // --- Compose State ---
  const [recipientType, setRecipientType] = useState("organizations"); // 'organizations' | 'manual' | 'both'
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [manualEmails, setManualEmails] = useState([]);
  const [manualEmailInput, setManualEmailInput] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");

  // Recipient resolution preview
  const [resolvedRecipients, setResolvedRecipients] = useState([]);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [resolving, setResolving] = useState(false);
  const [showRoster, setShowRoster] = useState(false);

  // Send Confirmation Modal
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);

  // Test Email Modal
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("developerportal@mindsherpa.health");
  const [sendingTest, setSendingTest] = useState(false);

  // --- History State ---
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);

  // Load organizations on mount
  useEffect(() => {
    loadOrganizations();
  }, []);

  // Load history whenever switching to History tab
  useEffect(() => {
    if (activeTab === 1) {
      loadHistory();
    }
  }, [activeTab]);

  // Debounced auto-resolve recipients when selections change
  useEffect(() => {
    const timer = setTimeout(() => {
      handleResolveRecipients();
    }, 300);
    return () => clearTimeout(timer);
  }, [selectedOrgs, manualEmails, recipientType]);

  const loadOrganizations = async () => {
    try {
      const res = await fetchEmailOrganizations();
      if (res && res.success) {
        setOrganizations(res.data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch organizations list");
    }
  };

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      const res = await fetchSentEmailHistory();
      if (res && res.success) {
        setHistory(res.data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load sent email history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleResolveRecipients = async () => {
    const companyIds =
      recipientType === "manual"
        ? []
        : selectedOrgs.map((o) => o.company_id);
    const emails =
      recipientType === "organizations" ? [] : manualEmails;

    if (companyIds.length === 0 && emails.length === 0) {
      setResolvedRecipients([]);
      setResolvedCount(0);
      return;
    }

    try {
      setResolving(true);
      const res = await resolveEmailRecipients({
        company_ids: companyIds,
        manual_emails: emails,
      });
      if (res && res.success) {
        setResolvedRecipients(res.recipient_emails || []);
        setResolvedCount(res.total_recipients || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setResolving(false);
    }
  };

  // Manual Email Chip Management
  const handleAddManualEmail = () => {
    if (!manualEmailInput.trim()) return;
    const parts = manualEmailInput.split(/[,;\s\n]+/);
    const newEmails = parts
      .map((p) => p.trim().toLowerCase())
      .filter((p) => p && p.includes("@") && !manualEmails.includes(p));

    if (newEmails.length > 0) {
      setManualEmails((prev) => [...prev, ...newEmails]);
      setManualEmailInput("");
    } else {
      toast.warn("Please enter a valid email address");
    }
  };

  const handleManualEmailKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      handleAddManualEmail();
    }
  };

  const handleRemoveManualEmail = (emailToRemove) => {
    setManualEmails((prev) => prev.filter((e) => e !== emailToRemove));
  };

  const handleSelectAllOrgs = () => {
    if (selectedOrgs.length === organizations.length) {
      setSelectedOrgs([]);
    } else {
      setSelectedOrgs([...organizations]);
    }
  };

  // Send Broadcast Submission
  const handleOpenSendConfirm = () => {
    if (!subject.trim()) {
      toast.error("Please enter an email subject");
      return;
    }
    if (!bodyHtml.trim() || bodyHtml === "<p></p>") {
      toast.error("Please enter the email body content");
      return;
    }
    if (resolvedCount === 0) {
      toast.error("No recipients resolved. Please select organizations or enter manual emails.");
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleConfirmSendBroadcast = async () => {
    try {
      setSendingBroadcast(true);
      const companyIds =
        recipientType === "manual"
          ? []
          : selectedOrgs.map((o) => o.company_id);
      const emails =
        recipientType === "organizations" ? [] : manualEmails;

      const payload = {
        subject: subject.trim(),
        body_html: bodyHtml.trim(),
        recipient_type: recipientType,
        company_ids: companyIds,
        manual_emails: emails,
        sent_by_email: "developerportal@mindsherpa.health",
      };

      const res = await sendEmailBroadcast(payload);
      if (res && res.success) {
        toast.success(
          `Broadcast queued! Sending to ${res.total_recipients} recipient(s).`
        );
        setConfirmDialogOpen(false);
        // Reset form
        setSubject("");
        setBodyHtml("");
        setSelectedOrgs([]);
        setManualEmails([]);
        setResolvedRecipients([]);
        setResolvedCount(0);
        // Switch to history tab
        setActiveTab(1);
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.error || "Failed to send email broadcast"
      );
    } finally {
      setSendingBroadcast(false);
    }
  };

  // Send Test Email
  const handleSendTestEmail = async () => {
    if (!subject.trim()) {
      toast.error("Please enter an email subject first");
      return;
    }
    if (!bodyHtml.trim()) {
      toast.error("Please enter email body content");
      return;
    }
    if (!testEmailAddress.trim() || !testEmailAddress.includes("@")) {
      toast.error("Please enter a valid test recipient email");
      return;
    }

    try {
      setSendingTest(true);
      const res = await sendTestEmail({
        subject: subject.trim(),
        body_html: bodyHtml.trim(),
        test_recipient: testEmailAddress.trim(),
      });
      if (res && res.success) {
        toast.success(`Test email sent to ${testEmailAddress}! Check your inbox.`);
        setTestDialogOpen(false);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "–";
    try {
      const d = new Date(dateStr);
      return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const filteredHistory = history.filter((item) => {
    const q = historySearch.toLowerCase();
    const sub = (item.subject || "").toLowerCase();
    const orgs = (item.organization_names || []).join(" ").toLowerCase();
    return sub.includes(q) || orgs.includes(q);
  });

  return (
    <Box
      sx={{
        display: "flex",
        width: "100vw",
        height: "100vh",
        maxWidth: "100vw",
        maxHeight: "100vh",
        overflow: "hidden",
        background: "#F8FAFC",
      }}
    >
      {/* Sidebar */}
      <Box sx={{ flexShrink: 0 }}>
        <SidebarCol />
      </Box>

      {/* Main Container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}
      >
        <MainHeader
          titleText="Developer Portal"
          subtitleText="Email Broadcast & Communications"
        />

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: { xs: 2, md: 4 },
            pb: 4,
            pt: 2.5,
          }}
        >
          {/* Breadcrumbs & Back Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                onClick={() => navigate("/dev")}
                startIcon={<ArrowBackIcon />}
                sx={{
                  color: "#64748B",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "14px",
                  p: 0,
                  minWidth: "auto",
                  mr: 1,
                  "&:hover": { color: "#1E293B", bgcolor: "transparent" },
                }}
              >
                Back
              </Button>
              <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                /
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#64748B",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={() => navigate("/dev")}
              >
                Developer
              </Typography>
              <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                /
              </Typography>
              <Typography variant="caption" sx={{ color: "#154C3A", fontWeight: 700 }}>
                Send Email
              </Typography>
            </Box>

            {/* View Tabs */}
            <Tabs
              value={activeTab}
              onChange={(_, val) => setActiveTab(val)}
              sx={{
                minHeight: 38,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  minHeight: 38,
                  py: 0.5,
                  px: 2,
                  borderRadius: 2,
                },
                "& .Mui-selected": {
                  color: "#154C3A !important",
                  bgcolor: "#ECFDF5",
                },
                "& .MuiTabs-indicator": {
                  bgcolor: "#154C3A",
                  height: 3,
                  borderRadius: 1.5,
                },
              }}
            >
              <Tab icon={<SendIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Compose & Send" />
              <Tab
                icon={<HistoryIcon sx={{ fontSize: 16 }} />}
                iconPosition="start"
                label={`Sent History (${history.length})`}
              />
            </Tabs>
          </Box>

          {/* ========================================================= */}
          {/* TAB 0: COMPOSE & SEND EMAIL                                */}
          {/* ========================================================= */}
          {activeTab === 0 && (
            <Box sx={{ maxWidth: 1000, mx: "auto" }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, md: 3.5 },
                  borderRadius: 3,
                  border: "1px solid #E2E8F0",
                  bgcolor: "#FFFFFF",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                {/* 1. Recipient Targeting Selector */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#1E293B" sx={{ mb: 1 }}>
                    1. Target Recipients
                  </Typography>

                  <FormControl component="fieldset" sx={{ mb: 1.5 }}>
                    <RadioGroup
                      row
                      value={recipientType}
                      onChange={(e) => setRecipientType(e.target.value)}
                    >
                      <FormControlLabel
                        value="organizations"
                        control={<Radio size="small" sx={{ color: "#154C3A", "&.Mui-checked": { color: "#154C3A" } }} />}
                        label={<Typography variant="body2" fontWeight={600}>Select Organizations (Companies)</Typography>}
                      />
                      <FormControlLabel
                        value="manual"
                        control={<Radio size="small" sx={{ color: "#154C3A", "&.Mui-checked": { color: "#154C3A" } }} />}
                        label={<Typography variant="body2" fontWeight={600}>Manual Email Addresses</Typography>}
                      />
                      <FormControlLabel
                        value="both"
                        control={<Radio size="small" sx={{ color: "#154C3A", "&.Mui-checked": { color: "#154C3A" } }} />}
                        label={<Typography variant="body2" fontWeight={600}>Both (Organizations + Custom)</Typography>}
                      />
                    </RadioGroup>
                  </FormControl>

                  {/* Organizations Selection */}
                  {(recipientType === "organizations" || recipientType === "both") && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Select one or more organizations to email all registered users in those organizations:
                        </Typography>
                        <Button
                          size="small"
                          onClick={handleSelectAllOrgs}
                          sx={{ textTransform: "none", fontSize: "12px", color: "#154C3A", fontWeight: 600 }}
                        >
                          {selectedOrgs.length === organizations.length ? "Deselect All" : "Select All Organizations"}
                        </Button>
                      </Box>

                      <Autocomplete
                        multiple
                        options={organizations}
                        disableCloseOnSelect
                        getOptionLabel={(option) => `${option.company_name} (${option.active_user_count} users)`}
                        value={selectedOrgs}
                        onChange={(_, newValue) => setSelectedOrgs(newValue)}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} key={option.company_id}>
                            <Checkbox
                              size="small"
                              checked={selected}
                              sx={{ mr: 1, color: "#154C3A", "&.Mui-checked": { color: "#154C3A" } }}
                            />
                            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                              <Typography variant="body2" fontWeight={600} color="#1E293B">
                                {option.company_name}
                              </Typography>
                              <Chip
                                size="small"
                                label={`${option.active_user_count} users`}
                                sx={{
                                  fontSize: "11px",
                                  height: 20,
                                  bgcolor: option.active_user_count > 0 ? "#ECFDF5" : "#F1F5F9",
                                  color: option.active_user_count > 0 ? "#059669" : "#94A3B8",
                                  fontWeight: 600,
                                }}
                              />
                            </Box>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={selectedOrgs.length === 0 ? "Search and select organizations..." : ""}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <BusinessIcon sx={{ color: "#64748B" }} />
                                  </InputAdornment>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Box>
                  )}

                  {/* Manual Emails Input */}
                  {(recipientType === "manual" || recipientType === "both") && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block", mb: 0.5 }}>
                        Enter custom recipient email addresses (press Enter or comma to add):
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <TextField
                          placeholder="e.g. user@company.com, team@partner.org"
                          size="small"
                          fullWidth
                          value={manualEmailInput}
                          onChange={(e) => setManualEmailInput(e.target.value)}
                          onKeyDown={handleManualEmailKeyDown}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MailOutlineIcon sx={{ color: "#64748B" }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Button
                          variant="outlined"
                          onClick={handleAddManualEmail}
                          disabled={!manualEmailInput.trim()}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            color: "#154C3A",
                            borderColor: "#154C3A",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Add Email
                        </Button>
                      </Box>

                      {/* Manual Emails Chips */}
                      {manualEmails.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mt: 1.5 }}>
                          {manualEmails.map((email) => (
                            <Chip
                              key={email}
                              label={email}
                              size="small"
                              onDelete={() => handleRemoveManualEmail(email)}
                              sx={{
                                bgcolor: "#EFF6FF",
                                color: "#1D4ED8",
                                fontWeight: 500,
                                fontSize: "12px",
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Resolved Recipients Summary Card */}
                  <Box
                    sx={{
                      p: 1.5,
                      px: 2,
                      bgcolor: resolvedCount > 0 ? "#F0FDF4" : "#F8FAFC",
                      border: "1px solid",
                      borderColor: resolvedCount > 0 ? "#BBF7D0" : "#E2E8F0",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <PeopleAltIcon sx={{ color: resolvedCount > 0 ? "#059669" : "#94A3B8" }} />
                      <Box>
                        <Typography variant="body2" fontWeight={700} color={resolvedCount > 0 ? "#166534" : "#64748B"}>
                          {resolving ? (
                            "Calculating recipients..."
                          ) : (
                            <>
                              {resolvedCount} Total Recipient{resolvedCount !== 1 ? "s" : ""} Resolved
                            </>
                          )}
                        </Typography>
                        <Typography variant="caption" color={resolvedCount > 0 ? "#15803D" : "#94A3B8"}>
                          {resolvedCount > 0
                            ? "All resolved users will receive an individual email delivery."
                            : "Select organizations or input email addresses to target recipients."}
                        </Typography>
                      </Box>
                    </Box>

                    {resolvedCount > 0 && (
                      <Button
                        size="small"
                        onClick={() => setShowRoster(!showRoster)}
                        endIcon={showRoster ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ textTransform: "none", fontSize: "12px", fontWeight: 600, color: "#166534" }}
                      >
                        {showRoster ? "Hide Recipient List" : "Preview Recipient List"}
                      </Button>
                    )}
                  </Box>

                  {/* Expandable Recipient Roster */}
                  <Collapse in={showRoster && resolvedCount > 0}>
                    <Box
                      sx={{
                        mt: 1,
                        p: 1.5,
                        bgcolor: "#FFFFFF",
                        border: "1px solid #BBF7D0",
                        borderRadius: 2,
                        maxHeight: 160,
                        overflowY: "auto",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.8,
                      }}
                    >
                      {resolvedRecipients.map((em, idx) => (
                        <Chip
                          key={idx}
                          label={em}
                          size="small"
                          sx={{ fontSize: "11px", height: 22, bgcolor: "#F0FDF4", color: "#166534" }}
                        />
                      ))}
                    </Box>
                  </Collapse>
                </Box>

                <Divider />

                {/* 2. Email Subject Line */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#1E293B" sx={{ mb: 1 }}>
                    2. Email Subject Line
                  </Typography>
                  <TextField
                    placeholder="e.g. Important Update: New Wellness Sessions & Resources Available"
                    fullWidth
                    required
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MailOutlineIcon sx={{ color: "#64748B" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* 3. Rich Text Email Body */}
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#1E293B">
                      3. Email Message Body
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Use the formatting toolbar to style text, add headings, insert links, or embed images
                    </Typography>
                  </Box>
                  <RichTextEditor
                    value={bodyHtml}
                    onChange={setBodyHtml}
                    placeholder="Write your email announcement or newsletter here... Add bold, headings, bullet points, or images."
                  />
                </Box>

                {/* 4. Action Footer */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pt: 2,
                    borderTop: "1px solid #E2E8F0",
                    flexWrap: "wrap",
                    gap: 1.5,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setTestDialogOpen(true)}
                    startIcon={<VisibilityIcon fontSize="small" />}
                    disabled={!subject.trim() || !bodyHtml.trim()}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      color: "#64748B",
                      borderColor: "#CBD5E1",
                      "&:hover": { bgcolor: "#F1F5F9", borderColor: "#94A3B8" },
                    }}
                  >
                    Send Test Email
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleOpenSendConfirm}
                    disabled={resolvedCount === 0 || !subject.trim() || !bodyHtml.trim()}
                    startIcon={<SendIcon />}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      bgcolor: "#154C3A",
                      "&:hover": { bgcolor: "#0D3327" },
                    }}
                  >
                    Send Broadcast Email ({resolvedCount})
                  </Button>
                </Box>
              </Paper>
            </Box>
          )}

          {/* ========================================================= */}
          {/* TAB 1: SENT EMAIL HISTORY                                  */}
          {/* ========================================================= */}
          {activeTab === 1 && (
            <Box sx={{ maxWidth: 1200, mx: "auto" }}>
              {/* Filter and Refresh Bar */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5, gap: 2 }}>
                <TextField
                  placeholder="Search sent emails by subject or organization..."
                  size="small"
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  sx={{ maxWidth: 400, bgcolor: "#FFFFFF", borderRadius: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#64748B" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={loadHistory}
                  disabled={historyLoading}
                  startIcon={historyLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, color: "#154C3A", borderColor: "#CBD5E1" }}
                >
                  Refresh
                </Button>
              </Box>

              {historyLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress sx={{ color: "#154C3A" }} />
                </Box>
              ) : filteredHistory.length === 0 ? (
                <Paper
                  sx={{
                    p: 6,
                    textAlign: "center",
                    borderRadius: 3,
                    border: "1px solid #E2E8F0",
                    bgcolor: "#FFFFFF",
                  }}
                >
                  <MailOutlineIcon sx={{ fontSize: 48, color: "#94A3B8", mb: 1.5 }} />
                  <Typography variant="h6" fontWeight={700} color="#1E293B">
                    No Sent Email Broadcasts
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
                    {historySearch ? "No records matched your search query." : "You haven't dispatched any broadcast emails yet."}
                  </Typography>
                  {!historySearch && (
                    <Button
                      variant="contained"
                      onClick={() => setActiveTab(0)}
                      startIcon={<SendIcon />}
                      sx={{ bgcolor: "#154C3A", textTransform: "none", borderRadius: 2, fontWeight: 600 }}
                    >
                      Compose First Broadcast
                    </Button>
                  )}
                </Paper>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid #E2E8F0",
                    overflow: "hidden",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                  }}
                >
                  <Table>
                    <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: "#475569", width: 170 }}>Date & Time</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Subject</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#475569", width: 220 }}>Target</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#475569", width: 120, textAlign: "center" }}>Recipients</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#475569", width: 100, textAlign: "center" }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#475569", width: 110, textAlign: "right" }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredHistory.map((item) => (
                        <TableRow key={item.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                          <TableCell sx={{ fontSize: "13px", color: "#64748B" }}>
                            {formatDate(item.created_at)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} color="#1E293B">
                              {item.subject}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {item.organization_names && item.organization_names.length > 0 ? (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {item.organization_names.map((org, i) => (
                                  <Chip
                                    key={i}
                                    label={org}
                                    size="small"
                                    sx={{ fontSize: "11px", height: 20, bgcolor: "#ECFDF5", color: "#059669", fontWeight: 600 }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Chip
                                label="Manual Emails"
                                size="small"
                                sx={{ fontSize: "11px", height: 20, bgcolor: "#EFF6FF", color: "#2563EB" }}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <Chip
                              label={`${item.recipient_count} users`}
                              size="small"
                              sx={{ fontSize: "12px", fontWeight: 700, bgcolor: "#F1F5F9", color: "#334155" }}
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <Chip
                              label={item.status || "Sent"}
                              size="small"
                              sx={{ fontSize: "11px", fontWeight: 700, bgcolor: "#ECFDF5", color: "#059669" }}
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: "right" }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setSelectedHistoryItem(item)}
                              startIcon={<VisibilityIcon fontSize="small" />}
                              sx={{
                                borderRadius: 1.5,
                                textTransform: "none",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "#154C3A",
                                borderColor: "#CBD5E1",
                              }}
                            >
                              Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* ========================================================= */}
      {/* CONFIRMATION DIALOG BEFORE SENDING BROADCAST               */}
      {/* ========================================================= */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => (sendingBroadcast ? undefined : setConfirmDialogOpen(false))}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 0.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#154C3A", borderBottom: "1px solid #E2E8F0", pb: 2 }}>
          Send Broadcast Email?
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="body2" color="#334155" sx={{ mb: 2 }}>
            You are about to dispatch an email broadcast to <strong>{resolvedCount} recipient(s)</strong>.
          </Typography>

          <Box sx={{ p: 2, bgcolor: "#F8FAFC", borderRadius: 2, border: "1px solid #E2E8F0", mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              SUBJECT:
            </Typography>
            <Typography variant="body2" fontWeight={700} color="#1E293B" sx={{ mb: 1 }}>
              {subject}
            </Typography>

            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              TARGET AUDIENCE:
            </Typography>
            <Typography variant="body2" color="#334155">
              {recipientType === "organizations" && `${selectedOrgs.length} Organization(s) selected`}
              {recipientType === "manual" && `${manualEmails.length} Manual Email(s)`}
              {recipientType === "both" && `${selectedOrgs.length} Organization(s) + ${manualEmails.length} Manual Email(s)`}
            </Typography>
          </Box>

          <Typography variant="caption" color="#64748B">
            Deliveries will be processed in the background via Google Gmail API. You can check delivery logs in Sent History.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#F8FAFC" }}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            disabled={sendingBroadcast}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: "none", color: "#64748B", borderColor: "#CBD5E1" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSendBroadcast}
            disabled={sendingBroadcast}
            variant="contained"
            startIcon={sendingBroadcast ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#154C3A",
              "&:hover": { bgcolor: "#0D3327" },
            }}
          >
            {sendingBroadcast ? "Sending Broadcast..." : "Yes, Send Broadcast"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================= */}
      {/* TEST EMAIL DIALOG                                          */}
      {/* ========================================================= */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#154C3A" }}>Send Test Email</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="#64748B" sx={{ mb: 2 }}>
            Send a single preview copy of your composed email to verify how it appears in your inbox:
          </Typography>
          <TextField
            label="Test Recipient Email"
            placeholder="your-email@example.com"
            fullWidth
            required
            size="small"
            value={testEmailAddress}
            onChange={(e) => setTestEmailAddress(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#F8FAFC" }}>
          <Button onClick={() => setTestDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSendTestEmail}
            disabled={sendingTest || !testEmailAddress.trim()}
            variant="contained"
            startIcon={sendingTest ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            sx={{ bgcolor: "#154C3A", textTransform: "none", fontWeight: 600 }}
          >
            {sendingTest ? "Sending..." : "Send Test Copy"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================= */}
      {/* SENT EMAIL DETAILS MODAL                                   */}
      {/* ========================================================= */}
      <Dialog
        open={Boolean(selectedHistoryItem)}
        onClose={() => setSelectedHistoryItem(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #E2E8F0",
            py: 2,
            px: 3,
            bgcolor: "#F8FAFC",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="#154C3A">
              Sent Broadcast Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Sent on {formatDate(selectedHistoryItem?.created_at)} &bull; #{selectedHistoryItem?.id}
            </Typography>
          </Box>
          <IconButton onClick={() => setSelectedHistoryItem(null)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, overflowY: "auto", maxHeight: "calc(80vh - 100px)" }}>
          {selectedHistoryItem && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* Subject & Meta */}
              <Box sx={{ p: 2, bgcolor: "#F8FAFC", borderRadius: 2, border: "1px solid #E2E8F0" }}>
                <Typography variant="subtitle1" fontWeight={700} color="#1E293B">
                  {selectedHistoryItem.subject}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1, fontSize: "13px", color: "#64748B" }}>
                  <span>
                    <strong>Total Delivered:</strong> {selectedHistoryItem.recipient_count} recipients
                  </span>
                  <span>
                    <strong>Target:</strong> {selectedHistoryItem.recipient_type}
                  </span>
                  {selectedHistoryItem.organization_names?.length > 0 && (
                    <span>
                      <strong>Organizations:</strong> {selectedHistoryItem.organization_names.join(", ")}
                    </span>
                  )}
                </Box>
              </Box>

              {/* Email HTML Body Preview */}
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="#1E293B" sx={{ mb: 1 }}>
                  Delivered Email Body:
                </Typography>
                <Box
                  dangerouslySetInnerHTML={{ __html: selectedHistoryItem.body_html }}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: "1px solid #E2E8F0",
                    bgcolor: "#FFFFFF",
                    fontSize: "15px",
                    lineHeight: 1.7,
                    color: "#1E293B",
                    "& img": { maxWidth: "100%", borderRadius: 2 },
                    "& a": { color: "#094B38", textDecoration: "underline", fontWeight: 600 },
                  }}
                />
              </Box>

              {/* Recipient Emails Roster */}
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="#1E293B" sx={{ mb: 1 }}>
                  Recipient Email Roster ({selectedHistoryItem.recipient_emails?.length || 0}):
                </Typography>
                <Box
                  sx={{
                    maxHeight: 140,
                    overflowY: "auto",
                    p: 1.5,
                    bgcolor: "#F8FAFC",
                    borderRadius: 2,
                    border: "1px solid #E2E8F0",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.8,
                  }}
                >
                  {(selectedHistoryItem.recipient_emails || []).map((e, idx) => (
                    <Chip key={idx} label={e} size="small" sx={{ fontSize: "11px", height: 22 }} />
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#F8FAFC" }}>
          <Button onClick={() => setSelectedHistoryItem(null)} variant="outlined" sx={{ borderRadius: 2, textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DevSendEmail;
